import { resolveEmailCidImages, type EmailInlineImageAttachment } from '../../../shared/emailLinkLabels';

export interface EmailMessageRenderable {
  id?: string | number;
  attachments?: EmailInlineImageAttachment[];
  body?: string | null;
  html?: string | null;
  text?: string | null;
  from?: string | null;
  subject?: string | null;
}

export interface EmailAttentionAssociation {
  entityType: string;
  status?: string | null;
}

export function escapeEmailPlainText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function emailContentLooksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

const EMAIL_TRACKING_PATH = '/t/email/open/';
const EMAIL_TRACKING_TOKEN = /^[1-9]\d{0,8}\.[A-Za-z0-9_-]{22}\.[A-Za-z0-9_-]{43}\.gif$/;

export function isEmailOwnerTrackingUrl(value: string): boolean {
  try {
    const url = new URL(value.trim(), 'https://email.invalid');
    const token = url.pathname.slice(EMAIL_TRACKING_PATH.length);
    return url.pathname.startsWith(EMAIL_TRACKING_PATH) && EMAIL_TRACKING_TOKEN.test(token);
  } catch {
    return false;
  }
}

export function suppressOwnerTrackingPixels(html: string): string {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const hasSrcset = /\bsrcset\s*=\s*/i.test(tag);
    if (hasSrcset) {
      const filtered = tag.replace(/\bsrcset\s*=\s*(?:"([^"]*)"|'([^']*)')/gi, (_match, doubleQuoted, singleQuoted) => {
        const value = String(doubleQuoted ?? singleQuoted ?? '');
        const candidates = value.split(',').filter((candidate) => !isEmailOwnerTrackingUrl(candidate.trim().split(/\s+/)[0]));
        return candidates.length ? `srcset="${candidates.join(', ')}"` : '';
      });
      return filtered.replace(/\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')/gi, (_match, doubleQuoted, singleQuoted) =>
        isEmailOwnerTrackingUrl(String(doubleQuoted ?? singleQuoted ?? '')) ? '' : _match);
    }
    const source = tag.match(/\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
    return source && isEmailOwnerTrackingUrl(source[1] ?? source[2] ?? '') ? '' : tag;
  });
}

export function buildEmailMessageHtml(email: EmailMessageRenderable): string {
  if (email.html?.trim()) {
    return resolveEmailCidImages(suppressOwnerTrackingPixels(email.html), email.id, email.attachments);
  }

  if (email.body?.trim()) {
    return emailContentLooksLikeHtml(email.body)
      ? resolveEmailCidImages(suppressOwnerTrackingPixels(email.body), email.id, email.attachments)
      : `<div style="white-space: pre-wrap;">${escapeEmailPlainText(email.body)}</div>`;
  }

  if (email.text?.trim()) {
    return `<div style="white-space: pre-wrap;">${escapeEmailPlainText(email.text)}</div>`;
  }

  return '<div style="color: #6b7280; font-style: italic;">Nessun contenuto disponibile</div>';
}

export function buildEmailPlainPreview(email: EmailMessageRenderable, maxLength = 180): string {
  const rawContent = email.text || email.body || email.html || '';
  const preview = rawContent
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!preview) return 'Nessun contenuto disponibile';
  return preview.length > maxLength ? `${preview.substring(0, maxLength)}...` : preview;
}

export function looksLikeAutomatedEmail(email: EmailMessageRenderable): boolean {
  const haystack = [email.from, email.subject].filter(Boolean).join(' ').toLowerCase();
  return [
    'newsletter',
    'no-reply',
    'noreply',
    'unsubscribe',
    'promo',
    'marketing',
    'notification',
    'notifica',
  ].some((token) => haystack.includes(token));
}

export function buildEmailAttentionWarning(
  email: EmailMessageRenderable,
  associations: EmailAttentionAssociation[] = [],
): string {
  const fullContent = [email.subject, email.text, email.body, email.html]
    .filter(Boolean)
    .join(' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const haystack = fullContent.toLowerCase();
  const warnings: string[] = [];
  const activeAssociations = associations.filter((association) => association.status !== 'deleted');
  const hasDealAssociation = activeAssociations.some((association) => association.entityType === 'deal');
  const hasAnyCrmAssociation = activeAssociations.length > 0;
  const isAutomated = looksLikeAutomatedEmail(email) || /(non rispondere|do not reply|no[- ]?reply)/i.test(haystack);

  const addWarning = (warning: string) => {
    if (!warnings.includes(warning)) warnings.push(warning);
  };

  if (/(urgente|scadenza|entro|deadline|asap|oggi|domani|venerdi|venerdì|questa settimana)/i.test(haystack)) {
    addWarning('Possibile scadenza o urgenza: verifica se serve una risposta entro una data.');
  }

  if (!isAutomated && /(rispond|attendo|feedback|fammi sapere|conferma|confermare|reply|let me know|confirm)/i.test(haystack)) {
    addWarning('Sembra richiedere risposta o conferma: non lasciarla senza follow-up.');
  }

  if (/(proposta formale|proposal|comitato|contratto|documentazione|allegat|offerta|preventivo)/i.test(haystack)) {
    addWarning('Contiene materiale operativo o proposta: controlla se devi preparare documenti o prossimi passi.');
  }

  if (!isAutomated && !hasDealAssociation && /(location|immobile|negozio|apertura|sviluppo|franchising|format|brand|retail)/i.test(haystack)) {
    addWarning('Tema commerciale/deal senza deal collegato: valuta manualmente se associarlo a un deal esistente.');
  }

  if (!hasAnyCrmAssociation && !isAutomated) {
    addWarning('Nessun collegamento CRM visibile: valuta se va collegata a contatto, azienda o deal esistente.');
  }

  if (warnings.length === 0) {
    return isAutomated
      ? 'Nessuna azione critica evidente: sembra comunicazione automatica o informativa.'
      : 'Nessuna cosa importante evidente non svolta: resta da leggere il contenuto prima di archiviare.';
  }

  return warnings.slice(0, 3).join(' ');
}

export function forceEmailLinksToNewTab(html: string): string {
  return html.replace(/<a\b([^>]*)>/gi, (_match, attributes: string) => {
    const withoutTarget = attributes.replace(/\s+target\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
    const withoutRel = withoutTarget.replace(/\s+rel\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
    return `<a${withoutRel} target="_blank" rel="noopener noreferrer">`;
  });
}
